import { io, type Socket } from 'socket.io-client';

import type { AccountSummary, CallRecord, Chat, ConnectorStatus, Message, Settings } from '../types';
import { apiOrigin } from './api';

type ServerEvents = {
  'chat:upsert': (chat: Chat) => void;
  'message:upsert': (message: Message) => void;
  'status:update': (status: ConnectorStatus) => void;
  'accounts:update': (accounts: AccountSummary[]) => void;
  'settings:update': (settings: Settings) => void;
  'features:update': (features: Record<string, boolean>) => void;
  'call:upsert': (call: CallRecord) => void;
  'avatar:update': (event: { callId: string; state: string; segment?: { speaker: 'user' | 'agent'; text: string } }) => void;
};

type RealtimeHandlers = {
  onChat: (chat: Chat) => void;
  onMessage: (message: Message) => void;
  onStatus: (status: ConnectorStatus) => void;
  onAccounts: (accounts: AccountSummary[]) => void;
  onSettings: (settings: Settings) => void;
  onFeatures: (features: Record<string, boolean>) => void;
  onCall: (call: CallRecord) => void;
  onAvatar: (event: { callId: string; state: string; segment?: { speaker: 'user' | 'agent'; text: string } }) => void;
  onConnection: (connected: boolean) => void;
};

class RealtimeClient {
  #socket: Socket<ServerEvents> | null = null;

  public subscribe(handlers: RealtimeHandlers): () => void {
    this.disconnect();
    const origin = apiOrigin || window.location.origin;
    const socket = io(origin, {
      autoConnect: false,
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 5_000,
      timeout: 10_000,
    });
    this.#socket = socket;
    socket.on('chat:upsert', handlers.onChat);
    socket.on('message:upsert', handlers.onMessage);
    socket.on('status:update', handlers.onStatus);
    socket.on('accounts:update', handlers.onAccounts);
    socket.on('settings:update', handlers.onSettings);
    socket.on('features:update', handlers.onFeatures);
    socket.on('call:upsert', handlers.onCall);
    socket.on('avatar:update', handlers.onAvatar);
    socket.on('connect', () => handlers.onConnection(true));
    socket.on('disconnect', () => handlers.onConnection(false));
    socket.on('connect_error', () => handlers.onConnection(false));
    socket.connect();
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      if (this.#socket === socket) this.#socket = null;
    };
  }

  public disconnect(): void {
    this.#socket?.removeAllListeners();
    this.#socket?.disconnect();
    this.#socket = null;
  }
}

export const realtime = new RealtimeClient();
